import { Flex, Group, Highlight } from "@mantine/core";
import {
  MantineReactTable,
  MRT_ExpandButton,
  useMantineReactTable,
  type MRT_ColumnDef
} from "mantine-react-table-open";
import { memo, useMemo } from "react";
import { getDefaultMRTOptions } from "../../../../mantine/getDefaultMRTOptions";
import type { FilterDropdownValues } from "../../../FilterDropdown/FilterDropdown.types";
import type {
  FlatLocationOverviewData,
  LocationOverviewData
} from "../LocationOverview.types";
import { LocationOverviewFilterDropdown } from "../LocationOverviewFilterDropdown/LocationOverviewFilterDropdown";
import { getDocumentFolderColumn } from "./columns/DocumentFolderColumn";
import { getLocationColumn } from "./columns/LocationColumn";
import { getMeteringDataColumns } from "./columns/MeteringDataColumns";
import { getMsbColumn } from "./columns/MsbColumn";
import { getSiteColumn } from "./columns/SiteColumn";
import { getUpdatedColumn } from "./columns/UpdatedColumn";

interface LocationOverviewTableProps {
  data: Array<LocationOverviewData>;
  isLoading: boolean;
  numRows: number;
  pageIndex: number;
  pageSize: number;
  onFetchData: ({ page, pageSize }: { page: number; pageSize: number }) => void;
  globalFilter: string;
  setGlobalFilter: (value: string) => void;
  selectedFilters: FilterDropdownValues;
  onChangeFilters: (newFilters: FilterDropdownValues) => void;
}

function LocationNumberCell({
  number,
  filter
}: {
  number: string | number;
  filter: string;
}) {
  return filter ? (
    <Highlight highlight={filter}>{String(number)}</Highlight>
  ) : (
    <>{number}</>
  );
}

function LocationOverviewTable({
  data,
  isLoading,
  numRows,
  pageIndex,
  pageSize,
  onFetchData,
  globalFilter,
  setGlobalFilter,
  selectedFilters,
  onChangeFilters
}: LocationOverviewTableProps) {
  const columns: Array<MRT_ColumnDef<FlatLocationOverviewData>> = [
    {
      accessorKey: "number",
      header: "Lokationsnummer",
      size: 275
    },
    getLocationColumn(globalFilter),
    getMsbColumn(),
    getSiteColumn(globalFilter),
    getDocumentFolderColumn(),
    ...getMeteringDataColumns(),
    getUpdatedColumn()
  ];

  const flatData: Array<FlatLocationOverviewData> = useMemo(
    () =>
      data.flatMap((item) =>
        item.singleOverviews.map((overview) => ({
          number: item.number,
          ...overview
        }))
      ),
    [data]
  );

  const table = useMantineReactTable<FlatLocationOverviewData>({
    ...getDefaultMRTOptions({
      initialState: {
        grouping: ["number"],
        expanded: true
      },
      className: "LocationOverviewTable",
      hideBorder: true,
      searchTextInput: {
        placeholder:
          "Suche nach Lokationsnummer, Lokationsname oder Liegenschaftsname"
      }
    }),
    enableGrouping: true,
    groupedColumnMode: "remove",
    enableSorting: false,
    state: {
      isLoading: isLoading,
      pagination: { pageIndex, pageSize },
      globalFilter,
      showGlobalFilter: true
    },
    positionToolbarAlertBanner: "none",

    manualPagination: true,
    rowCount: numRows,
    onPaginationChange: (pagination) => {
      if (typeof pagination === "function") {
        const currentState = { pageIndex, pageSize };
        const newState = pagination(currentState);
        onFetchData({ page: newState.pageIndex, pageSize: newState.pageSize });
      }
    },

    renderTopToolbarCustomActions: () => (
      <Group ps="sm" w="100%" wrap="nowrap">
        <LocationOverviewFilterDropdown
          selectedFilters={selectedFilters}
          onChangeFilters={onChangeFilters}
        />
      </Group>
    ),

    displayColumnDefOptions: {
      "mrt-row-expand": {
        Cell: ({ row, table }) =>
          row.getIsGrouped() ? (
            <Flex align="center" gap="0.25rem">
              <MRT_ExpandButton row={row} table={table} />
              <LocationNumberCell
                filter={globalFilter}
                number={row.groupingValue as string}
              />
              <span>({row.subRows?.length})</span>
            </Flex>
          ) : null
      }
    },
    mantineExpandAllButtonProps: () => ({
      display: "none"
    }),
    manualFiltering: true,
    onGlobalFilterChange: setGlobalFilter,
    columns,
    data: flatData
  });

  return <MantineReactTable table={table} />;
}

const MemoizedLocationOverviewTable = memo(LocationOverviewTable);

export {
  MemoizedLocationOverviewTable as LocationOverviewTable,
  LocationOverviewTableProps
};
